import { EvaluateExpression, FormatExpressionValue } from '../../expressionEngine';
import { FormatPercent, FormatPrice } from '../../utils/numberUtils';

/**
 * CellValue — única fonte que componentes (e sort/export) usam pra extrair o
 * VALOR de uma célula a partir de um row do pipeline.
 *
 * REGRAS:
 *   - Só lê `row.X` (campos do contrato). NUNCA recomputa de séries cruas.
 *   - Não tem fallbacks numéricos (window=30 etc) — tudo já está pre-computado
 *     no Selector via DeriveCtx.
 *   - Custom columns (`custom:<id>`) avaliam expressão via expressionEngine.
 *
 * @param {import('../contract').Row} row
 * @param {string} col
 * @param {{customColumnsById?: Map<string, Object>}|null} ctx
 * @returns {number|string|null|undefined|Array}
 */
export function GetCellValue(row, col, ctx = null) {
    if (typeof col === 'string' && col.startsWith('custom:')) {
        return evalCustomCell(row, col, ctx);
    }
    switch (col) {
        case 'thumb':
            // URL da foto (export CSV); a célula renderiza <img>.
            return row.thumbnail || null;
        case 'stockTotal':
            return (row.bling || 0) + (row.mlAvail || 0);
        case 'avgPeriod':
            return Number(row.avgPeriod || 0);
        case 'periodTotal':
            // Unidades vendidas no PERÍODO ativo (reflete 30/60/90/custom).
            return Number(row.periodTotal || 0);
        case 'orderCount':
            // Pedidos distintos no período ativo (paid+cancelled+other), pré-
            // computado no Selector. Valida 1:1 com paging.total de
            // /orders/search?item=MLB (sem filtro de status) — assim como
            // periodTotal soma unidades de todos os status.
            return Number(row.orderCount || 0);
        case 'bestDay':
            return Number(row.bestDay || 0);
        case 'forecast':
            return Number(row.forecast || 0);
        case 'coverage':
            return Number(row.coverage || 0);
        case 'fullCoverage':
            // null = sem demanda recente → Sort manda pro fim, célula "—".
            return row.fullCoverage === null || row.fullCoverage === undefined ? null : Number(row.fullCoverage);
        case 'fullNeed':
            return Number(row.fullNeed || 0);
        case 'fullSuggest':
            return Number(row.fullSuggest || 0);
        case 'fullAnalysis':
            // Tags do Full (array) — export junta com vírgula; célula renderiza badges.
            return Array.isArray(row.fullTags) ? row.fullTags : [];
        case 'margin': {
            if (!row.price || row.price === 0) return '0%';
            return FormatPercent(((row.price - row.cost) / row.price) * 100);
        }
        case 'weekGrowth':
            return FormatPercent(Number(row.weekGrowth || 0));
        case 'monthVariation':
            return FormatPercent(Number(row.monthVariation || 0));
        case 'periodSplit':
            return FormatPercent(Number(row.periodSplit || 0));
        case 'cancelPercent':
            return FormatPercent(Number(row.cancelPercent || 0));
        case 'price':
            return FormatPrice(Number(row.price || 0));
        case 'cost':
            return FormatPrice(Number(row.cost || 0));
        default:
            return row[col];
    }
}

function evalCustomCell(row, col, ctx) {
    const id = col.slice('custom:'.length);
    const def = ctx?.customColumnsById?.get(id);
    if (!def) return '—';
    try {
        const result = EvaluateExpression(def.expression, row);
        return FormatExpressionValue(result, def.format);
    } catch {
        return '—';
    }
}
